import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { PhotoManager } from "component/shared/photo-manager";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	getClientPropertyById,
	getClientPropertyListItems,
	getClientPropertyFlags,
	getClientPropertiesLoaded,
	getAgents,
} from "redux/selector";
import {
	fetchClientProperties,
	fetchClientProperty,
	createClientProperty,
	updateClientProperty,
} from "redux/slice/client-properties";
import { UpsertClientPropertyResource } from "api/resource/client-property";
import { SupplementalStatus } from "type/supplemental-status";
import { coldFusionReplatform } from "shared/env";
import * as env from "shared/env";
import * as paths from "routes/paths";
import { SupplementalForm } from "./supplemental-form";

interface Props extends PageProps, Router.RouteComponentProps {}

const TITLE = "Supplemental Listings";

const openDetail = (detailUrl: string) => {
	const url = /^https?:\/\//i.test(detailUrl) ? detailUrl : `${env.coldFusionUrl}${detailUrl}`;
	window.open(url, "_blank");
};

const SupplementalEditBase: React.FC<Props> = ({ user, history, match, location }) => {
	const dispatch = useDispatch();
	const idParam = (match.params as { id?: string }).id;
	const id = idParam ? Number(idParam) : undefined;
	const isNew = id === undefined;

	const loaded = useSelector((state: RootState) => getClientPropertiesLoaded(state));
	const flags = useSelector((state: RootState) => getClientPropertyFlags(state));
	const agentResources = useSelector((state: RootState) => getAgents(state)) || [];
	const property = useSelector((state: RootState) =>
		id != null ? getClientPropertyById(state, id) : undefined
	);
	// detailUrl (public listing page) lives on the lean list row, not the edit resource — pull it from
	// the list we already load for flags.
	const listItems = useSelector((state: RootState) => getClientPropertyListItems(state));
	const detailUrl = useMemo(
		() => (id != null ? listItems.find((item) => item.id === id)?.detailUrl : undefined),
		[listItems, id]
	);

	const agentOptions = useMemo(
		() =>
			agentResources.map((agent) => ({
				id: agent.id,
				name: [agent.lastName, agent.firstName].filter(Boolean).join(", ") || `Agent ${agent.id}`,
			})),
		[agentResources]
	);

	// MAX-4194: seed the free-text agent box from a stored AID when no typed name exists.
	//
	// Roughly 451 of the 460 protected rows carry an agent id with empty name columns. Without
	// this the box renders blank while both public pages show that agent, and no user action can
	// reach it — a blank box over an empty column is indistinguishable from "nothing to do".
	//
	// Resolved here, not in the form: the form only receives `agentOptions`, whose `name` is
	// "Last, First" — a *display* format. ClientProperty.getAgentName() builds "First Last", so
	// seeding from agentOptions.name would store AGT_FRSTNM = "Smith," the first time the user
	// edits. Format from the raw agent resource to match what the server would have produced.
	//
	// Do NOT substitute the agent's derived `name` selector: it yields "" for a last-name-only
	// agent where getAgentName() yields the last name.
	//
	// `property` is undefined on the Add route, hence the guard.
	const agentNameSeed = useMemo(() => {
		if (!property || property.agentName || (property.agentId ?? 0) <= 0) {
			return "";
		}
		const assigned = agentResources.find((agent) => agent.id === property.agentId);
		return assigned ? [assigned.firstName, assigned.lastName].filter(Boolean).join(" ") : "";
	}, [property, agentResources]);

	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [savedMessage, setSavedMessage] = useState<string | null>(null);
	const [tab, setTab] = useState(0);

	// Post-create nav lands here with ?message= (CF returns to the new record's photo tab); show it once.
	useEffect(() => {
		const message = new URLSearchParams(location.search).get("message");
		if (message) {
			setSavedMessage(message);
		}
	}, [location.search]);

	// The client-level UI flags (agent-page column, office account) arrive only on the list wrapper
	// response — so a direct load / refresh of this route needs the list fetched to render the agent
	// block correctly. Navigating in from the list already has them in state; this covers deep links.
	useEffect(() => {
		if (!loaded) {
			dispatch(fetchClientProperties() as any);
		}
	}, [loaded, dispatch]);

	useEffect(() => {
		if (id != null) {
			dispatch(fetchClientProperty({ id }) as any);
		}
	}, [id, dispatch]);

	const goToList = (message?: string) =>
		history.push(
			message
				? `${paths.listingsSupplemental}?message=${encodeURIComponent(message)}`
				: paths.listingsSupplemental
		);

	// MAX-4194: returns whether the save actually landed. The form needs to know, because it
	// re-baselines its agent-name dirty check on success — and this function catches its own
	// errors, so awaiting it is not on its own evidence that anything was written.
	const handleSave = async (body: UpsertClientPropertyResource): Promise<boolean> => {
		setSaving(true);
		setError(null);
		try {
			if (isNew) {
				// F4: .unwrap() so an API failure rejects here rather than resolving and navigating away.
				const created = await dispatch(createClientProperty({ body }) as any).unwrap();
				setSaving(false);
				// CF returns to the new record's photo tab after create (cpropupdate.cfm:166-176); the
				// native editor renders the Photos card inline, so land on that record's edit page.
				history.push(
					`${Router.generatePath(paths.listingsSupplementalEdit, { id: created.id })}?message=${encodeURIComponent("Supplemental listing created")}`
				);
				return true;
			} else {
				await dispatch(updateClientProperty({ id: id as number, body }) as any).unwrap();
				setSaving(false);
				// CF stays on the still-open edit form after update — show the success message in place.
				setSavedMessage("Modifications Successful");
				return true;
			}
		} catch{
			setSaving(false);
			setError(isNew ? "Failed to create listing" : "Failed to save listing");
			return false;
		}
	};

	// On edit, wait for the full record before mounting the form so fields aren't briefly blank.
	const loadingProperty = !isNew && !property;

	return (
		<DashboardLayout
			permitted={user && user.permissions.supplementalListings}
			title={TITLE}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Home />
					</FeatherIcon>
					{TITLE}
				</Mui.Typography>
			}
		>
			{/* Address title + Type/Status summary, with View Property Page — CF summary block
			    (cpropmodify_output_1.cfm:116-135). Edit mode only (no address/photos yet on add). */}
			{property && (
				<Mui.Box mb={2} display="flex" justifyContent="space-between" alignItems="flex-start">
					<div>
						<Mui.Typography variant="h6">
							{[property.streetNumber, property.streetName].filter(Boolean).join(" ")}
							{" , "}
							{(property.cityName || "").toUpperCase()}, {property.state} {property.zip}
						</Mui.Typography>
						<Mui.Typography variant="body2" color="textSecondary">
							Type: {property.propertyType} | Status:{" "}
							{SupplementalStatus.getById(property.status)?.label || property.status}
						</Mui.Typography>
					</div>
					{detailUrl && (
						<Mui.Button variant="contained" onClick={() => openDetail(detailUrl)}>
							View Property Page
						</Mui.Button>
					)}
				</Mui.Box>
			)}

			{/* CF's editor is a two-tab layout: Listing Information | Photos (MAX-3939). The Photos tab only
			    exists once the listing has been saved (CF hides it in add mode), so a brand-new listing
			    shows just the form. Both panels stay mounted (hidden when inactive) so unsaved edits survive
			    a tab switch. */}
			{isNew ? (
				<SupplementalForm
					property={null}
					flags={{ agentControl: flags.agentControl }}
					agentNameSeed={agentNameSeed}
					agents={agentOptions}
					saving={saving}
					onSave={handleSave}
					onCancel={() => goToList()}
				/>
			) : loadingProperty ? (
				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.CircularProgress />
					</FullPageCard>
				</Mui.Box>
			) : (
				<>
					<Mui.Tabs
						value={tab}
						onChange={(_e, value) => setTab(value)}
						indicatorColor="primary"
						textColor="primary"
					>
						<Mui.Tab label="Listing Information" />
						<Mui.Tab label="Photos" />
					</Mui.Tabs>

					<Mui.Box mt={2} hidden={tab !== 0}>
						<SupplementalForm
							key={id}
						property={property || null}
							flags={{ agentControl: flags.agentControl }}
							agentNameSeed={agentNameSeed}
							agents={agentOptions}
							saving={saving}
							onSave={handleSave}
							onCancel={() => goToList()}
						/>
					</Mui.Box>

					<Mui.Box mt={2} hidden={tab !== 1}>
						{property && (
							<FullPageCard>
								<PhotoManager listingKey={{ clientPropertyId: property.id }} hasBoard={false} inline />
							</FullPageCard>
						)}
					</Mui.Box>
				</>
			)}

			<Mui.Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)}>
				<MuiAlert severity="error">
					<Mui.Typography>{error}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>

			<Mui.Snackbar open={!!savedMessage} autoHideDuration={4000} onClose={() => setSavedMessage(null)}>
				<MuiAlert severity="success">
					<Mui.Typography>{savedMessage}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const SupplementalEditPageBase: React.FC<Props> = (props) => {
	// Native edit route only exists under the replatform flag; otherwise CF owns the editor inside its
	// own iframe, so send deep links back to the list route (which renders the CF iframe when off).
	if (!coldFusionReplatform) {
		return <Router.Redirect to={paths.listingsSupplemental} />;
	}
	return <SupplementalEditBase {...props} />;
};

export const SupplementalEditPage = SupplementalEditPageBase;
